import { isEmpty } from "ramda";
import { ProposalByIndicationFlow } from "~/contexts/indicationFlow/types";
import {
  EPersonType,
  EProposalFileType,
  EProposalStatus,
  GetProposalAndIndicationByIdQuery,
  ProposalCustomerPendenciesFields,
  ProposalFileModel,
  ProposalLegalRepresentativePendenciesFields,
  ProposalModel,
} from "~/graphql/types";
import { Unarray } from "~/utils";

export type AddressForm = {
  city: string;
  state: string;
  street: string;
  number: string;
  zipcode: string;
  complement: string;
  neighborhood: string;
};

export type LegalRepresentativeType = {
  //strings
  name: string;
  email: string;
  phone: string;
  document: string;
  address: AddressForm;
  identificationNumber: string;
  pendencies?:
    | Omit<ProposalLegalRepresentativePendenciesFields, "country">
    | null
    | undefined;
  files?: ProposalFileModel[];

  //files
  customerPjRepresentativeDocumentWithPhotoBack: null | File[];
  customerPjRepresentativeDocumentWithPhotoFront: null | File[];
};

export const LEGAL_REPRESENTATIVE_INITIAL_VALUES: LegalRepresentativeType = {
  name: "",
  email: "",
  phone: "",
  document: "",
  identificationNumber: "",
  address: {
    city: "",
    state: "",
    number: "",
    street: "",
    zipcode: "",
    complement: "",
    neighborhood: "",
  },
  customerPjRepresentativeDocumentWithPhotoBack: null,
  customerPjRepresentativeDocumentWithPhotoFront: null,
};

type PendencyObject = NonNullable<
  GetProposalAndIndicationByIdQuery["getIndicationRelatedEntitiesById"]["proposal"]
>["customerRef"]["pendencies"];

export type BasicDataPendency = "name" | "document" | "email" | "phone";

export const hasPendencyOrNullOnBasicData = (
  fieldName: BasicDataPendency,
  pendencies: PendencyObject,
) => {
  if (!pendencies) {
    return true;
  }

  if (!pendencies[fieldName].sendedAt) {
    return true;
  }

  if (!pendencies[fieldName].rejectedAt) {
    return true;
  }

  return false;
};

export type AddressPendency =
  | "zipcode"
  | "state"
  | "city"
  | "neighborhood"
  | "street"
  | "number";

export const hasPendencyOrNullOnAddress = (
  fieldName: AddressPendency,
  pendencies: PendencyObject,
) => {
  if (!pendencies) {
    return true;
  }

  if (!pendencies[fieldName].sendedAt) {
    return false;
  }

  if (pendencies[fieldName].rejectedAt) {
    return false;
  }

  return true;
};
export const hasPendencyOrNullOnDocuments = <
  T extends keyof ProposalCustomerPendenciesFields,
>(
  fieldName: T,
  pendencies: PendencyObject,
) => {
  if (!pendencies) {
    return true;
  }

  if (!pendencies[fieldName as keyof typeof pendencies]?.sendedAt) {
    return false;
  }

  if (pendencies[fieldName as keyof typeof pendencies]?.rejectedAt) {
    return false;
  }

  return true;
};

export type LegalRepresentativyPendency = keyof Omit<
  ProposalLegalRepresentativePendenciesFields,
  "country" | "complement"
>;

export const hasPendencyOrNullOnLegalRepresentativy = (
  fieldName: LegalRepresentativyPendency,
  pendencies?:
    | Omit<ProposalLegalRepresentativePendenciesFields, "country">
    | null
    | undefined,
) => {
  if (!pendencies) {
    return false;
  }

  if (!pendencies[fieldName].sendedAt) {
    return false;
  }

  if (pendencies[fieldName].rejectedAt) {
    return false;
  }

  return true;
};

export const hasDuplicatedValuesInLegalRepresentativies = (array: any[]) => {
  const identificationNumber = array.map(function (value) {
    return value.identificationNumber;
  });

  const document = array.map(function (value) {
    return value.document;
  });

  const phone = array.map(function (value) {
    return value.phone;
  });

  const email = array.map(function (value) {
    return value.email;
  });

  const myValues = [...identificationNumber, ...document, ...phone, ...email];

  return myValues.some(function (value, index, myArray) {
    if (value?.length > 1) {
      return myArray.indexOf(value) !== myArray.lastIndexOf(value);
    }
  });
};

type FileProposal = Unarray<
  NonNullable<
    GetProposalAndIndicationByIdQuery["getIndicationRelatedEntitiesById"]["proposal"]
  >["files"]
>;

export const documentFileIsDisabled = (file: FileProposal | undefined) => {
  if (!file?.sendAt) {
    return false;
  }

  if (file?.rejectedAt) {
    return false;
  }

  return true;
};

export const shouldDisableStep = (
  label: string,
  proposal: ProposalByIndicationFlow | null,
) => {
  if (!proposal) {
    return label !== "Dados básicos";
  }

  // Desativa o step 4 de "Formalização" se o status da proposta não for formalização e for igual a cancelado com termo de gestão = null
  if (
    label === "Assinatura do cliente" &&
    proposal.status !== EProposalStatus.formalization &&
    proposal.status !== EProposalStatus.finalized &&
    proposal.status !== EProposalStatus.canceled
  ) {
    return true;
  }

  return false;
};

export const isStepCompleted = (
  label: string,
  proposal: ProposalByIndicationFlow | null,
) => {
  if (!proposal) {
    return false;
  }

  if (proposal.status === EProposalStatus.finalized) {
    return true;
  }

  const { customerRef, files, energyBillInfo } = proposal;
  const {
    name,
    personType,
    legalRepresentative,
    address: customerAddress,
    email,
    phone,
    uf,
    dealershipId,
    averageEnergyBillValue,
  } = customerRef;

  if (label === "Dados básicos") {
    const baseValidation = Boolean(
      name &&
      document &&
      email &&
      phone &&
      uf &&
      dealershipId &&
      averageEnergyBillValue,
    );

    return baseValidation;
  }
  if (label === "Endereço") {
    return Boolean(
      customerAddress?.zipcode &&
      customerAddress?.street &&
      customerAddress?.neighborhood &&
      customerAddress?.city &&
      customerAddress?.state &&
      customerAddress?.number,
    );
  }
  if (label === "Representantes") {
    return !isEmpty(legalRepresentative);
  }
  if (label === "Documentos") {
    const hasEnergyBillDocument = Boolean(
      files?.find((item) => item.type === EProposalFileType.energyBill)?.sendAt,
    );
    const hasEnegyBillInfo = Boolean(
      energyBillInfo?.tension &&
      energyBillInfo?.AmountCents &&
      // energyBillInfo?.tusdValue &&
      // energyBillInfo?.energyTaxes &&
      energyBillInfo?.distributionTaxes &&
      energyBillInfo?.clientNumber &&
      energyBillInfo?.installationNumber &&
      energyBillInfo?.tariffClassification &&
      energyBillInfo?.address,
    );

    if (personType === EPersonType.pf) {
      const hasFrontCustomerDocument = Boolean(
        files?.find(
          (item) =>
            item.type === EProposalFileType.customerPfDocumentWithPhotoFront,
        )?.sendAt,
      );
      const hasBackCustomerDocument = Boolean(
        files?.find(
          (item) =>
            item.type === EProposalFileType.customerPfDocumentWithPhotoBack,
        )?.sendAt,
      );

      return Boolean(
        hasEnegyBillInfo &&
        hasFrontCustomerDocument &&
        hasBackCustomerDocument &&
        hasEnergyBillDocument,
      );
    }

    const hasCustomerPjCardDocument = Boolean(
      files?.find(
        (item) => item.type === EProposalFileType.customerPjDocumentCard,
      )?.sendAt,
    );
    const hasSocialContractDocument = Boolean(
      files?.find(
        (item) => item.type === EProposalFileType.customerPjSocialContract,
      )?.sendAt,
    );

    return Boolean(
      hasCustomerPjCardDocument &&
      hasSocialContractDocument &&
      hasEnegyBillInfo &&
      hasEnergyBillDocument,
    );
  }
  if (label === "Formalização") {
    return false;
  }
};
